import { logger } from '../helpers/logger';

const errorMessage =
    'Sorry, there was a problem fulfilling your request. Please try again and if the problem persists, contact support.';

/**
 * Custom error that represents an error returned from the server during an API call.
 */
export default class ServerError extends Error {
    static DefaultErrorMessage = errorMessage;

    isTimeoutError = false;
    name = 'ServerError';

    /**
     * Constructs a new instance of a ServerError object.
     * @param {Object} response - Fetch Response interface (see https://developer.mozilla.org/en-US/docs/Web/API/Response)
     */
    constructor(response, url) {
        super(errorMessage);

        // maintains proper stack trace for where our error was thrown (not available in IE)
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, ServerError);
        }

        this.json = null;
        this.status = response?.status;
        this.statusText = response?.statusText;
        this.url = response?.url || url;
        this.serverStackTrace = null;
        this.responseJson = undefined;

        if (
            response?.json &&
            response?.headers &&
            response?.headers.get('Content-Type')?.includes('application/json') // also allows 'application/json; charset=utf-8'
        ) {
            this.json = async () => {
                // if already parsed, don't parse again
                if (this.responseJson !== undefined) return this.responseJson;
                let responseJson = null;
                try {
                    responseJson = await response.json(); // this is a function that returns a promise

                    // Compose a technical error message with HTTP status and API route
                    if (this.message === ServerError.DefaultErrorMessage) {
                        let technicalMessage = '';

                        // Add HTTP status information if available
                        if (this.status && this.statusText) {
                            technicalMessage = `HTTP status code: ${this.status} - ${this.statusText}:`;
                        } else if (this.status) {
                            technicalMessage = `HTTP status code: ${this.status}:`;
                        }
                        // Add the API route that caused the issue (without query string for security)
                        if (this.url) {
                            try {
                                const urlObj = new URL(this.url);
                                const urlWithoutQuery = `${urlObj.origin}${urlObj.pathname}`;
                                technicalMessage += ` ${urlWithoutQuery}`;
                            } catch {
                                // If URL parsing fails, use the full URL as fallback
                                technicalMessage += ` ${this.url}`;
                            }
                        }

                        // Add server error message if available
                        if (responseJson?.message) {
                            technicalMessage += responseJson.message
                                ? ` - ${responseJson.message}`
                                : '';
                        }

                        // Append validation errors if they exist
                        if (responseJson?.validationErrors) {
                            const validationErrorsConcat = Object.keys(
                                responseJson.validationErrors,
                            )
                                ?.map((key) => `${key}: ${responseJson.validationErrors[key]}`)
                                ?.join(', ');
                            if (validationErrorsConcat) {
                                technicalMessage += ` (Validation errors: ${validationErrorsConcat})`;
                            }
                        }

                        // Update the message only if we have meaningful technical information
                        if (technicalMessage) {
                            this.message = technicalMessage;
                        }
                    }
                } catch {
                    logger.warn('ServerError: Failed to parse response JSON.');
                }
                this.responseJson = responseJson;
                return this.responseJson;
            };
        }

        this.getServerStackTrace = async () => {
            if (!this.json) return this.serverStackTrace;

            // parse stacktrace from the fetch API response object
            const result = await this.json();
            if (result.wasSuccessful === false && result.message)
                this.serverStackTrace = result.message;

            return this.serverStackTrace;
        };
    }
}
